import FileUploader from "@via/components/file-uploader/file-uploader";
import Modal from "@via/components/modal.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component";
import { ModalSize } from "@via/utils/constants.tsx";
import { type FC, useEffect, useMemo, useState } from "react";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import { useParams } from "react-router-dom";
import { fileUpload } from "@via/pages/data/data-sources/cloud-data-sources/common/file-upload.ts";
import SingleFileSpreadsheet from "@via/pages/projects/runs/overview/inputs/input-file/single-file-spreadsheet.tsx";
import UpdateSpreadsheetColumnsModal from "@via/pages/projects/runs/overview/inputs/input-file/update-spreadsheet-columns-modal.tsx";
import AddNewColumnModal from "@via/pages/projects/runs/overview/inputs/input-file/add-new-column-modal.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { prepareBucketPath } from "@via/pages/data/data-sources/data-source.api";
import {
  AwsFileUploadPath,
  SshFileUploadPath,
  GcsFileUploadPath,
  ExecutorJobsType,
  RunEnvironmentAvailableListItem,
  type SaveRunInputs,
} from "common";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import { useGetUploadedFile } from "@via/pages/projects/runs/runs.api.tsx";
import { useDebounceCallback } from "usehooks-ts";
import {
  getRunUploadDir,
  runInputPathAfterUpload,
  versionedSpreadsheetFileName,
} from "@via/pages/projects/runs/utils/helpers.tsx";
import { uploadTargetForRun } from "@via/pages/data/data-sources/ssh-data-source/agent-listing-notices.ts";

interface InputSingleFileModalProps {
  form: UseFormReturn;
  value: Record<string, unknown>;
  setValue: (value: Record<string, unknown> | null) => void;
  onSave: (inputId: string, value: string | null) => void;
  editor?: Record<string, any>;
  runUUID?: string | null;
  paramsInputs?: SaveRunInputs[];
}

interface FormData {
  value: string | null;
}

const InputSingleFileModal: FC<InputSingleFileModalProps> = ({
  form,
  value,
  setValue,
  onSave,
  editor,
  runUUID,
  paramsInputs,
}) => {
  const { runId = "" } = useParams();
  const { setNotification } = useNotifications();
  const [isLoading, setIsLoading] = useState(false);
  const [isSpreadsheetFileUploading, setIsSpreadsheetFileUploading] =
    useState(false);
  const [progressPercentageList, setProgressPercentageList] = useState<
    { filename: string; progress: number }[]
  >([]);
  const [spreadsheetData, setSpreadsheetData] = useState<string[][]>([]);
  const [defaultSpreadsheetData, setDefaultSpreadsheetData] =
    useState<string[][]>();
  const [
    isUpdateSpreadsheetColumnsModalOpen,
    setIsUpdateSpreadsheetColumnsModalOpen,
  ] = useState(false);
  const [isAddNewColumnModalOpen, setIsAddNewColumnModalOpen] = useState(false);
  const [spreadsheetColumn, setSpreadsheetColumn] = useState<{
    columnIndex: number;
    columns: string[];
    insertPosition?: "left" | "right";
  }>();

  const {
    mutate: getUploadedFile,
    data: uploadedFile,
    isPending: isUploadedFileLoading,
    isError: isUploadedFileError,
    error: uploadedFileError,
  } = useGetUploadedFile(runId);

  const selectedRunEnvironment = form.watch("selectedRunEnvironment");

  const watchedSomeSpecificFormValues = useWatch({
    name: ["launchDirectory", "publishDir", "workDirectory"],
    control: form.control,
  });

  const [launchDirectory, publishDir, workDirectory] =
    watchedSomeSpecificFormValues;

  const isCloudJob =
    selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch ||
    selectedRunEnvironment?.executorJob === ExecutorJobsType.AwsBatch;

  const isAgentEnvironment = selectedRunEnvironment?.transport === "agent";
  // agent-5-5: only an agent environment takes a target; SSH keeps today's behavior.
  const uploadTarget = isAgentEnvironment
    ? uploadTargetForRun(runId)
    : undefined;

  const runUploadDir = useMemo(() => {
    return getRunUploadDir({
      isCloudJob,
      workDirectory,
      publishDir,
      launchDirectory,
      runId,
    });
  }, [isCloudJob, workDirectory, publishDir, launchDirectory, runId]);

  const isDisabledUpload = useMemo(() => {
    return !selectedRunEnvironment || !runUploadDir;
  }, [selectedRunEnvironment, runUploadDir]);

  const credential = useMemo(() => {
    if (selectedRunEnvironment?.executorJob === ExecutorJobsType.AwsBatch) {
      return {
        id: selectedRunEnvironment.amazonCreId,
        path: AwsFileUploadPath,
        type: ExecutorJobsType.AwsBatch,
      };
    } else if (
      selectedRunEnvironment?.executorJob === ExecutorJobsType.GoogleBatch
    ) {
      return {
        id: selectedRunEnvironment.googleCreId,
        path: GcsFileUploadPath,
        type: ExecutorJobsType.GoogleBatch,
      };
    } else {
      return {
        id: selectedRunEnvironment?.id,
        path: SshFileUploadPath,
        type: ExecutorJobsType.Local,
      };
    }
  }, [selectedRunEnvironment]);

  const uploadCredentials = useMemo(() => {
    const credentials = {
      prefix: "",
      savedCredentialId: credential.id,
      bucketName: runUploadDir,
    };
    prepareBucketPath(credentials);
    // After prepareBucketPath is fixed, we can remove this condition
    if (credential.type === ExecutorJobsType.Local) {
      credentials.prefix = "/" + credentials.prefix;
    }
    return credentials;
  }, [runUploadDir, credential]);

  const {
    control,
    handleSubmit,
    setValue: setFormValue,
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      value: Object.values(value as Record<string, string>)[0] ?? null,
    },
  });

  const path = Object.values(value as Record<string, string>)?.[0];

  const debouncedGetUploadedFile = useDebounceCallback(() => {
    if (path && editor && selectedRunEnvironment) {
      getUploadedFile({
        path,
        profileClusterId: selectedRunEnvironment.id,
        ...(runUUID && { runUUID }),
      });
    }
  }, 300);

  useEffect(() => {
    debouncedGetUploadedFile();
  }, [selectedRunEnvironment, path, editor, runUUID]);

  useEffect(() => {
    if (path && uploadedFile) {
      const firstLine = uploadedFile.split("\n")[0]; // Get the first line of the file
      const separator = firstLine.includes("\t") ? "\t" : ","; // Determine separator based on first line

      const parsedData = uploadedFile
        .split("\n")
        .map(row => row.split(separator));

      setDefaultSpreadsheetData(parsedData);
    }
  }, [path, uploadedFile]);

  // fileUpload reports through callbacks only, and a failure never calls the success
  // handler: it clears the loading flag instead. This turns that into a promise the
  // confirm can await, rejecting when loading ends without a success or when the
  // upload never starts (fileUpload refuses an empty or oversized file up front).
  const uploadSpreadsheetFile = (file: File): Promise<string | undefined> =>
    new Promise<string | undefined>((resolve, reject) => {
      let uploaded = false;
      const started = fileUpload(
        credential.path,
        [file],
        uploadCredentials,
        setNotification,
        (placedPath?: string) => {
          uploaded = true;
          resolve(placedPath);
        },
        loading => {
          setIsSpreadsheetFileUploading(loading);
          if (!loading && !uploaded) {
            reject(new Error("Spreadsheet upload failed"));
          }
        },
        setProgressPercentageList,
        uploadTarget
      );
      if (!started) {
        reject(new Error("Spreadsheet upload did not start"));
      }
    });

  const saveSingleFile = async (payload: FormData): Promise<boolean> => {
    const trimmedValue = payload.value?.trim() ?? null;

    if (
      editor &&
      !isUploadedFileError &&
      !isDisabledUpload &&
      (!trimmedValue || trimmedValue === path)
    ) {
      const file = createFileFromSpreadsheetData();
      // Nothing to upload has always left the dialog open; false keeps it that way.
      if (!file) return false;
      const placedPath = await uploadSpreadsheetFile(file);
      setProgressPercentageList([]);
      const fileName = file.name.split("/").slice(-1).join("");
      onSave(
        Object.keys(value)[0],
        runInputPathAfterUpload(runUploadDir, fileName, placedPath)
      );
      return true;
    }

    onSave(Object.keys(value)[0], trimmedValue);
    return true;
  };

  // Resolving true hands the close to Modal. A failed spreadsheet upload rejects
  // (handleSubmit rethrows it), which keeps the dialog open with the upload's
  // notification on screen. The parent's onSave used to close the dialog itself,
  // while this handler never settled at all.
  const handleConfirmModal = async (): Promise<boolean> => {
    // Undefined would close the dialog, so the guard has to say false.
    if (isDisabledUpload) return false;

    let saved = false;
    await handleSubmit(async (payload: FormData) => {
      saved = await saveSingleFile(payload);
    })();
    return saved;
  };

  const handleFileUpload = (files: File[]): void => {
    const uploadedFile = files[0];
    const uploadedFileName = uploadedFile.name.split("/").slice(-1).join("");

    const reader = new FileReader();

    reader.onload = event => {
      const content = (event.target?.result as string) || ""; // Ensure content is not null or undefined
      if (!content.trim()) {
        console.warn("File is empty.");
        return;
      }

      const firstLine = content.split("\n")[0]; // Get the first line of the file
      const separator = firstLine.includes("\t") ? "\t" : ","; // Determine separator based on first line

      const parsedData = content.split("\n").map(row => row.split(separator));

      // Set default spreadsheet data
      setDefaultSpreadsheetData(parsedData);
    };

    reader.onerror = error => {
      console.error("Error reading file:", error);
    };

    reader.readAsText(uploadedFile); // Read file content

    fileUpload(
      credential.path,
      files,
      uploadCredentials,
      setNotification,
      (placedPath?: string) =>
        handleFileUploadSuccess(uploadedFileName, placedPath),
      setIsLoading,
      setProgressPercentageList,
      uploadTarget
    );
  };

  // An upload from the file picker saves the input and closes the dialog without a
  // confirm, so this is the one close that stays outside Modal's confirm path.
  const handleFileUploadSuccess = (
    uploadedFileName: string,
    placedPath?: string
  ) => {
    const newPath = runInputPathAfterUpload(
      runUploadDir,
      uploadedFileName,
      placedPath
    );
    setProgressPercentageList([]);
    setFormValue("value", newPath);
    onSave(Object.keys(value)[0], newPath);
    setValue(null);
  };

  const updateSpreadsheetData = (data: string[][]): void => {
    setSpreadsheetData(data);
  };

  const createFileFromSpreadsheetData = (): File | null => {
    const path = Object.values(value as Record<string, string>)?.[0];
    const currentFileExtension = path?.split(".")?.pop()?.toLowerCase();

    if (!spreadsheetData.length) {
      console.warn("No spreadsheet data available to create a file.");
      return null;
    }

    const separator = editor?.separator === "tab" ? "\t" : ",";
    const fileExtension = editor?.separator === "tab" ? "tsv" : "csv";
    const fileContent = spreadsheetData
      .map(row =>
        row.join(
          currentFileExtension === "tsv"
            ? "\t"
            : currentFileExtension === "csv"
              ? ","
              : separator
        )
      )
      .join("\n");
    const baseFileName = editor?.filename ?? `data.${fileExtension}`;
    // agent-5-5, decision D-a: placement on an agent never overwrites, so every save there is a new
    // file under a fresh name, and the input moves to it only after the upload succeeded.
    const fileName = isAgentEnvironment
      ? versionedSpreadsheetFileName(baseFileName)
      : baseFileName;

    try {
      const file = new File(
        [fileContent],
        `${uploadCredentials.prefix}${fileName}`,
        {
          type: `text/${fileExtension}`,
        }
      );
      return file;
    } catch (error) {
      console.error("Failed to create file from spreadsheet data:", error);
      return null;
    }
  };

  const getUploadValidationMessages = ({
    selectedRunEnvironment,
    runUploadDir,
  }: {
    selectedRunEnvironment: RunEnvironmentAvailableListItem | null;
    runUploadDir: string;
  }): string[] => {
    if (!selectedRunEnvironment) {
      return ["You should select run environment!"];
    }

    if (!runUploadDir) {
      return [
        isCloudJob
          ? "For AWS/Google Batch environments, you must enable Publish Directory or provide a valid Work Directory path!"
          : "You must provide a valid Execution Directory or Work Directory path!",
      ];
    }

    return [];
  };

  return (
    <>
      <Modal
        open={!!value}
        setOpen={() => setValue(null)}
        title="Change Single Input File"
        confirmText="Save"
        isConfirmDisabled={isDisabledUpload}
        onConfirm={handleConfirmModal}
        isConfirmLoading={isLoading || isSpreadsheetFileUploading}
        size={ModalSize.Large}>
        <form onSubmit={e => e.preventDefault()}>
          {isDisabledUpload && (
            <MessageBanner
              messages={getUploadValidationMessages({
                selectedRunEnvironment,
                runUploadDir,
              })}
              type={BannerType.Warning}
            />
          )}

          <div className="my-5">
            <FormInputComponent
              control={control}
              name="value"
              label="File Location"
              placeholder="You can enter the full path of a file, Amazon (S3) or Google (GS) location"
            />
            <div className="mt-4 flex align-items">
              <FileUploader
                onUpload={handleFileUpload}
                maxFiles={1}
                {...(editor ? { maxFileSize: 1 * 1024 * 1024 } : {})} // Limit file size to 1 MB for spreadsheets
                isLoading={isLoading}
                progressPercentageList={progressPercentageList}
                prefix={uploadCredentials.prefix}
                isDisabled={isDisabledUpload || isUploadedFileLoading}
              />
            </div>
            {editor && isUploadedFileError && (
              <div className="mt-4">
                <MessageBanner
                  messages={[
                    `The editor could not open this file. ${
                      uploadedFileError?.response?.data?.message ??
                      "Check the file location and try again."
                    }`,
                  ]}
                  type={BannerType.Warning}
                />
              </div>
            )}
            {editor && !isUploadedFileError && (
              <SingleFileSpreadsheet
                data={defaultSpreadsheetData}
                form={form}
                columns={defaultSpreadsheetData?.[0] || editor?.default?.[0]}
                updateColumns={(columnIndex, columns) => {
                  setDefaultSpreadsheetData(spreadsheetData);
                  setIsUpdateSpreadsheetColumnsModalOpen(true);
                  setSpreadsheetColumn({ columnIndex, columns });
                }}
                insertColumnLeft={(columnIndex, columns) => {
                  setDefaultSpreadsheetData(spreadsheetData);
                  setIsAddNewColumnModalOpen(true);
                  setSpreadsheetColumn({
                    columnIndex,
                    columns,
                    insertPosition: "left",
                  });
                }}
                insertColumnRight={(columnIndex, columns) => {
                  setDefaultSpreadsheetData(spreadsheetData);
                  setIsAddNewColumnModalOpen(true);
                  setSpreadsheetColumn({
                    columnIndex,
                    columns,
                    insertPosition: "right",
                  });
                }}
                deleteColumn={columnIndex => {
                  const updatedData = [...spreadsheetData];
                  updatedData.forEach(row => row.splice(columnIndex, 1));
                  setDefaultSpreadsheetData(updatedData);
                }}
                collectionAutofill={editor?.collection_autofill}
                onChange={updateSpreadsheetData}
                isLoading={isUploadedFileLoading}
                paramsInputs={paramsInputs}
              />
            )}
          </div>
        </form>
        <UpdateSpreadsheetColumnsModal
          open={isUpdateSpreadsheetColumnsModalOpen}
          setOpen={setIsUpdateSpreadsheetColumnsModalOpen}
          spreadsheetColumn={spreadsheetColumn}
          submitSpreadsheetColumn={(columnIndex, updatedColumnName) => {
            const updateColumns = data => {
              const updatedData = [...data];
              updatedData[0][columnIndex] = updatedColumnName;
              return updatedData;
            };

            setDefaultSpreadsheetData(prevData => {
              const dataToUpdate =
                prevData ?? editor?.default ?? spreadsheetData;
              return updateColumns(dataToUpdate);
            });
          }}
        />
        <AddNewColumnModal
          open={isAddNewColumnModalOpen}
          setOpen={setIsAddNewColumnModalOpen}
          spreadsheetColumn={spreadsheetColumn}
          submitSpreadsheetColumn={updatedColumnName => {
            const insertColumn = (data, columnIndex, position) => {
              const updatedData = data.map((row, rowIndex) => {
                const newRow = [...row];
                const insertAt =
                  position === "left" ? columnIndex : columnIndex + 1;
                if (rowIndex === 0) {
                  newRow.splice(insertAt, 0, updatedColumnName);
                } else {
                  newRow.splice(insertAt, 0, "");
                }
                return newRow;
              });
              return updatedData;
            };

            setDefaultSpreadsheetData(prevData => {
              const dataToInsert =
                prevData ?? editor?.default ?? spreadsheetData;
              return insertColumn(
                dataToInsert,
                spreadsheetColumn?.columnIndex,
                spreadsheetColumn?.insertPosition
              );
            });
          }}
        />
      </Modal>
    </>
  );
};

export default InputSingleFileModal;
